import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import { EditorContent, useEditor } from '@tiptap/react';
import { Extension, type Editor } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { RichTextEditor } from './rich-text-editor';
import { ContentPreview } from './content-preview';
import { inlineTranslationExtensions } from './inline-translation-extensions';
import { translatedInlineBlock, translationBatches, translationFragmentHTML } from './translation-draft';
import { api } from '@/lib/api';
import {
  translationBlocks,
  translationBlockId,
  translationPlainText,
  translationSourceHash,
  translationTextHash,
  translationState,
  type ContentLanguage,
  type FieldTranslationMetadata,
  type LocalizedText,
  type TiptapBlock,
  type TranslationCopy,
  type TranslationReview,
  type TranslationState,
} from '@fame/shared';

const languages: ContentLanguage[] = ['nl', 'fr', 'en'];
const names: Record<ContentLanguage, string> = { nl: 'Nederlands (BE)', fr: 'Frans', en: 'Engels' };
const states: Record<TranslationState, string> = {
  missing: 'Ontbreekt',
  changed: 'Bron gewijzigd — nakijken',
  unreviewed: 'Nog nakijken',
  reviewed: 'Nagekeken',
};
type Field = {
  key: string;
  label: string;
  value: LocalizedText;
  setValue: (value: LocalizedText) => void;
  multiline?: boolean;
  halfWidth?: boolean;
};
type Props = {
  kind: 'blog' | 'case';
  isNew: boolean;
  sourceLanguage: ContentLanguage;
  onSourceLanguageChange: (language: ContentLanguage) => void;
  content: Record<string, unknown>;
  onContentChange: (content: Record<string, unknown>) => void;
  translations: Partial<Record<ContentLanguage, TranslationCopy>>;
  onTranslationsChange: (value: Partial<Record<ContentLanguage, TranslationCopy>>) => void;
  fieldMetadata: FieldTranslationMetadata;
  onFieldMetadataChange: (value: FieldTranslationMetadata) => void;
  fields: Field[];
  draft: Record<string, unknown>;
  settings: ReactNode;
  actions: ReactNode;
  guardDisabled?: boolean;
};
type Unit = { key: string; label: string; sourceHash: string; text: string; block?: TiptapBlock; field?: Field };
type Snapshot = { source: string; target: string };
type Proposal = {
  language: ContentLanguage;
  key: string;
  snapshot: Snapshot;
  value: string | TiptapBlock;
  selected: boolean;
};
type UndoUnit = {
  language: ContentLanguage;
  key: string;
  before: unknown;
  metadata?: TranslationReview;
  after: Snapshot;
  copyLanguagePresent: boolean;
  metadataLanguagePresent: boolean;
};

// Inline wording is editable, but link destinations and attributes belong to the master.
function sourceBoundInlineContent(source: TiptapBlock, value?: TiptapBlock) {
  const sourceLinks: Array<Record<string, unknown>> = [];
  let previousHref: unknown;
  for (const node of Array.isArray(source.content) ? source.content : []) {
    const link = (node.marks ?? []).find((mark: { type: string }) => mark.type === 'link');
    if (link && link.attrs?.href !== previousHref) sourceLinks.push(link.attrs ?? {});
    previousHref = link?.attrs?.href;
  }
  const content = Array.isArray(value?.content) ? structuredClone(value.content) : [];
  previousHref = undefined;
  let index = -1;
  for (const node of content) {
    const link = (node.marks ?? []).find((mark: { type: string }) => mark.type === 'link');
    const href = link?.attrs?.href;
    if (link && href !== previousHref) index++;
    if (link) {
      if (sourceLinks[index]) link.attrs = { ...sourceLinks[index] };
      else node.marks = node.marks.filter((mark: { type: string }) => mark.type !== 'link');
    }
    previousHref = href;
  }
  return content;
}

function InlineCopy({
  source,
  value,
  onChange,
  onFocus,
  label,
}: {
  source: TiptapBlock;
  value?: TiptapBlock;
  onChange: (value: TiptapBlock) => void;
  onFocus: (editor: Editor) => void;
  label: string;
}) {
  const latest = useRef({ source, onChange, onFocus });
  useLayoutEffect(() => {
    latest.current = { source, onChange, onFocus };
  }, [source, onChange, onFocus]);
  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions: [
        ...inlineTranslationExtensions,
        Extension.create({
          name: 'translationStructure',
          addProseMirrorPlugins() {
            return [
              new Plugin({
                filterTransaction(transaction) {
                  if (!transaction.docChanged) return true;
                  const json = transaction.doc.toJSON();
                  const blocks = json.content ?? [];
                  if (blocks.length !== 1 || blocks[0].type !== source.type) return false;
                  const sourceLinks: string[] = [];
                  for (const node of Array.isArray(source.content) ? source.content : []) {
                    for (const mark of node.marks ?? []) if (mark.type === 'link') sourceLinks.push(mark.attrs?.href);
                  }
                  return (blocks[0].content ?? []).every(
                    (node: { type: string; marks?: Array<{ type: string; attrs?: { href?: string } }> }) =>
                      (node.type === 'text' || node.type === 'hardBreak') &&
                      (node.marks ?? []).every(
                        mark => mark.type !== 'link' || sourceLinks.includes(mark.attrs?.href ?? '')
                      )
                  );
                },
              }),
            ];
          },
        }),
      ],
      content: { type: 'doc', content: [{ ...source, content: sourceBoundInlineContent(source, value) }] },
      editorProps: {
        attributes: { 'aria-label': label },
        handleKeyDown: (_view, event) => event.key === 'Enter' && !event.shiftKey,
      },
      onFocus: ({ editor: current }) => latest.current.onFocus(current),
      onSelectionUpdate: ({ editor: current }) => latest.current.onFocus(current),
      onUpdate: ({ editor: current }) =>
        latest.current.onChange({
          ...latest.current.source,
          content: sourceBoundInlineContent(latest.current.source, current.getJSON().content?.[0]),
        }),
    },
    [source]
  );
  useEffect(() => {
    if (!editor) return;
    const document = { type: 'doc', content: [{ ...source, content: sourceBoundInlineContent(source, value) }] };
    const normalized = editor.schema.nodeFromJSON(document).toJSON();
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(normalized))
      editor.commands.setContent(document, { emitUpdate: false });
  }, [editor, source, value]);
  return (
    <EditorContent
      editor={editor}
      className="rounded-md border bg-background p-3 [&_.tiptap]:min-h-12 [&_.tiptap]:outline-none"
    />
  );
}

/** A single master document, with global language context and transient translation proposals. */
export function ContentEditingShell(props: Props) {
  const { sourceLanguage, fields, content, translations, fieldMetadata } = props;
  const [confirmed, setConfirmed] = useState(!props.isNew);
  const [locked, setLocked] = useState(!props.isNew);
  const [language, setLanguage] = useState(sourceLanguage);
  const [compare, setCompare] = useState(false);
  const [filter, setFilter] = useState<'all' | TranslationState>('all');
  const [active, setActive] = useState<string>();
  const [inlineEditor, setInlineEditor] = useState<Editor>();
  const [started, setStarted] = useState<ContentLanguage[]>(() =>
    languages.filter(
      target =>
        target !== sourceLanguage &&
        (fields.some(field => field.value[target]?.trim()) ||
          Object.values(translations[target] ?? {}).some(entry => translationPlainText(entry.block).trim()))
    )
  );
  const [dialog, setDialog] = useState(false);
  const [targets, setTargets] = useState<ContentLanguage[]>([]);
  const [method, setMethod] = useState<'manual' | 'automatic'>('automatic');
  const [scope, setScope] = useState<'missing' | 'changed' | 'selected'>('missing');
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [progress, setProgress] = useState<{ completed: number; total: number }>();
  const [undo, setUndo] = useState<UndoUnit[]>();
  const [notice, setNotice] = useState('');
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const latest = useRef(props);
  useLayoutEffect(() => {
    latest.current = props;
  }, [props]);
  const mounted = useRef(true);
  const generation = useRef(0);
  const requestActive = useRef(false);
  const [baseline] = useState(() => JSON.stringify(props.draft));
  const isDirty =
    proposals.length > 0 || Boolean(progress) || (!props.guardDisabled && JSON.stringify(props.draft) !== baseline);
  const blocker = useBlocker(isDirty);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    const header = headerRef.current;
    if (!confirmed || !header) return;
    setHeaderHeight(header.getBoundingClientRect().height);
    if (!window.ResizeObserver) return;
    const observer = new window.ResizeObserver(() => setHeaderHeight(header.getBoundingClientRect().height));
    observer.observe(header);
    return () => observer.disconnect();
  }, [confirmed]);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [isDirty]);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('Je hebt niet-opgeslagen wijzigingen of vertaalvoorstellen. Wil je deze pagina verlaten?'))
      blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  const unitsFor = (value: Props): Unit[] => [
    ...value.fields.map(field => ({
      key: field.key,
      label: field.label,
      text: field.value[value.sourceLanguage] ?? '',
      sourceHash: translationTextHash(field.value[value.sourceLanguage] ?? ''),
      field,
    })),
    ...translationBlocks(value.content).map(block => ({
      key: block.id,
      label: block.block.type === 'heading' ? 'Kop' : 'Alinea',
      text: block.text,
      sourceHash: block.sourceHash,
      block: block.block,
    })),
  ];
  const units = unitsFor(props);
  const applicable = units.filter(unit => unit.text.trim());
  const unitIdentity = applicable.map(unit => `${unit.key}:${unit.block?.type ?? 'field'}`).join('|');
  useEffect(() => {
    const current = unitsFor(latest.current).filter(unit => unit.text.trim());
    setProposals(previous =>
      previous.some(
        proposal =>
          !current.some(
            unit =>
              unit.key === proposal.key &&
              (!unit.block || (typeof proposal.value === 'object' && proposal.value.type === unit.block.type))
          )
      )
        ? previous.filter(proposal =>
            current.some(
              unit =>
                unit.key === proposal.key &&
                (!unit.block || (typeof proposal.value === 'object' && proposal.value.type === unit.block.type))
            )
          )
        : previous
    );
    setUndo(previous => {
      if (!previous || previous.every(item => current.some(unit => unit.key === item.key))) return previous;
      const remaining = previous.filter(item => current.some(unit => unit.key === item.key));
      return remaining.length ? remaining : undefined;
    });
  }, [unitIdentity]);
  useEffect(() => {
    if (!dialog) return;
    const previous = document.activeElement;
    const modal = document.querySelector<HTMLElement>('[aria-labelledby="translation-dialog-title"]');
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modal) return;
      const controls = [
        ...modal.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
        ),
      ];
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => {
      document.removeEventListener('keydown', trap);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [dialog]);
  const targetFor = (value: Props, unit: Unit, target: ContentLanguage) =>
    unit.field ? (unit.field.value[target] ?? '') : value.translations[target]?.[unit.key]?.block;
  const metadataFor = (value: Props, unit: Unit, target: ContentLanguage): TranslationReview | undefined =>
    unit.field ? value.fieldMetadata[target]?.[unit.key] : value.translations[target]?.[unit.key];
  const snapshot = (value: Props, unit: Unit, target: ContentLanguage): Snapshot => ({
    source: unit.sourceHash,
    target: JSON.stringify({ value: targetFor(value, unit, target), metadata: metadataFor(value, unit, target) }),
  });
  const unitState = (unit: Unit, target: ContentLanguage) => {
    const value = targetFor(props, unit, target);
    const text = typeof value === 'string' ? value : translationPlainText(value);
    const hash = typeof value === 'string' ? translationTextHash(value) : value ? translationSourceHash(value) : '';
    return translationState(unit.sourceHash, hash, Boolean(text.trim()), metadataFor(props, unit, target));
  };
  const counts = (target: ContentLanguage) => {
    const result = { missing: 0, changed: 0, unreviewed: 0, reviewed: 0 };
    for (const unit of applicable) result[unitState(unit, target)]++;
    return result;
  };
  const outdated = (proposal: Proposal, value = props) => {
    const unit = unitsFor(value).find(item => item.key === proposal.key && item.text.trim());
    if (!unit) return true;
    const current = snapshot(value, unit, proposal.language);
    return current.source !== proposal.snapshot.source || current.target !== proposal.snapshot.target;
  };
  const changeField = (field: Field, text: string) => {
    if (text.trim()) setLocked(true);
    field.setValue({ ...field.value, [language]: text });
    if (language !== sourceLanguage) {
      const current = fieldMetadata[language]?.[field.key];
      props.onFieldMetadataChange({
        ...fieldMetadata,
        [language]: {
          ...fieldMetadata[language],
          [field.key]: {
            sourceHash: current?.sourceHash ?? translationTextHash(field.value[sourceLanguage] ?? ''),
            origin: 'manual',
          },
        },
      });
    }
  };
  const changeBlock = (unit: Unit, block: TiptapBlock) => {
    const current = latest.current;
    const entry = current.translations[language]?.[unit.key];
    current.onTranslationsChange({
      ...current.translations,
      [language]: {
        ...current.translations[language],
        [unit.key]: { block, sourceHash: entry?.sourceHash ?? unit.sourceHash, origin: 'manual' },
      },
    });
  };

  // Apply all units in one React event; never let field updates overwrite sibling fields or other languages.
  const apply = (
    changes: Array<{
      unit: Unit;
      language: ContentLanguage;
      value: unknown;
      metadata?: TranslationReview;
      pruneCopyLanguage?: boolean;
      pruneMetadataLanguage?: boolean;
    }>
  ) => {
    const current = latest.current;
    const copies = { ...current.translations };
    const metadata = { ...current.fieldMetadata };
    const fieldValues: Record<string, LocalizedText> = {};
    for (const change of changes) {
      if (change.unit.field) {
        fieldValues[change.unit.key] = { ...(fieldValues[change.unit.key] ?? change.unit.field.value) };
        if (typeof change.value === 'string') fieldValues[change.unit.key][change.language] = change.value;
        else delete fieldValues[change.unit.key][change.language];
        metadata[change.language] = { ...metadata[change.language] };
        if (change.metadata) metadata[change.language]![change.unit.key] = change.metadata;
        else delete metadata[change.language]![change.unit.key];
      } else {
        copies[change.language] = { ...copies[change.language] };
        if (change.value && typeof change.value === 'object') {
          // Values originate from the typed editor/proposal boundary, not network JSON.
          const block = change.value as TiptapBlock;
          copies[change.language]![change.unit.key] = {
            block,
            sourceHash: change.metadata?.sourceHash ?? change.unit.sourceHash,
            origin: change.metadata?.origin,
            reviewedHash: change.metadata?.reviewedHash,
          };
        } else delete copies[change.language]![change.unit.key];
      }
    }
    for (const change of changes) {
      if (change.pruneCopyLanguage && !Object.keys(copies[change.language] ?? {}).length)
        delete copies[change.language];
      if (change.pruneMetadataLanguage && !Object.keys(metadata[change.language] ?? {}).length)
        delete metadata[change.language];
    }
    for (const field of current.fields) if (fieldValues[field.key]) field.setValue(fieldValues[field.key]);
    current.onTranslationsChange(copies);
    current.onFieldMetadataChange(metadata);
    return {
      ...current,
      translations: copies,
      fieldMetadata: metadata,
      fields: current.fields.map(field => ({ ...field, value: fieldValues[field.key] ?? field.value })),
    };
  };
  const approve = (chosen: Unit[]) => {
    apply(
      chosen
        .filter(unit => unit.text.trim() && unitState(unit, language) !== 'missing')
        .map(unit => {
          const value = targetFor(props, unit, language);
          return {
            unit,
            language,
            value,
            metadata: {
              sourceHash: unit.sourceHash,
              origin: metadataFor(props, unit, language)?.origin ?? 'unknown',
              reviewedHash: typeof value === 'string' ? translationTextHash(value) : translationSourceHash(value!),
            },
          };
        })
    );
  };
  const accept = (chosen: Proposal[]) => {
    if (chosen.some(proposal => outdated(proposal, latest.current))) {
      setNotice('Een voorstel is verouderd. Vraag een nieuw voorstel; nieuwere tekst blijft behouden.');
      return;
    }
    const current = latest.current;
    const currentUnits = unitsFor(current);
    const before: UndoUnit[] = [];
    const changes = chosen.map(proposal => {
      const unit = currentUnits.find(item => item.key === proposal.key)!;
      before.push({
        language: proposal.language,
        key: proposal.key,
        before: structuredClone(
          unit.field ? unit.field.value[proposal.language] : targetFor(current, unit, proposal.language)
        ),
        metadata: structuredClone(metadataFor(current, unit, proposal.language)),
        after: proposal.snapshot,
        copyLanguagePresent: Object.prototype.hasOwnProperty.call(current.translations, proposal.language),
        metadataLanguagePresent: Object.prototype.hasOwnProperty.call(current.fieldMetadata, proposal.language),
      });
      return {
        unit,
        language: proposal.language,
        value: proposal.value,
        metadata: { sourceHash: unit.sourceHash, origin: 'automatic' as const },
      };
    });
    const after = apply(changes);
    setUndo(
      before.map(item => ({
        ...item,
        after: snapshot(
          after,
          unitsFor(after).find(unit => unit.key === item.key)!,
          item.language
        ),
      }))
    );
    setProposals(previous => previous.filter(proposal => !chosen.includes(proposal)));
    setNotice('Voorstellen overgenomen in het concept. Nog niet opgeslagen of nagekeken.');
  };
  const undoAcceptance = () => {
    if (!undo) return;
    const current = latest.current;
    const currentUnits = unitsFor(current);
    if (
      undo.some(item => {
        const unit = currentUnits.find(unit => unit.key === item.key);
        if (!unit) return true;
        const value = snapshot(current, unit, item.language);
        return value.source !== item.after.source || value.target !== item.after.target;
      })
    ) {
      setNotice(
        'Ongedaan maken is niet beschikbaar: bron, tekst of review is intussen gewijzigd. Je nieuwere werk blijft behouden.'
      );
      return;
    }
    apply(
      undo.map(item => ({
        unit: currentUnits.find(unit => unit.key === item.key)!,
        language: item.language,
        value: item.before,
        metadata: item.metadata,
        pruneCopyLanguage: !item.copyLanguagePresent,
        pruneMetadataLanguage: !item.metadataLanguagePresent,
      }))
    );
    setUndo(undefined);
    setNotice('De volledige overname is ongedaan gemaakt.');
  };

  const openDialog = (
    target = language === sourceLanguage ? languages.find(item => item !== sourceLanguage)! : language
  ) => {
    setTargets([target]);
    setMethod('automatic');
    setScope('missing');
    setDialog(true);
  };
  const scopedUnits = (target: ContentLanguage) =>
    applicable.filter(unit =>
      scope === 'selected' ? selectedUnits.includes(unit.key) : unitState(unit, target) === scope
    );
  const request = async (requestedTargets: ContentLanguage[], requested: (target: ContentLanguage) => Unit[]) => {
    if (requestActive.current) return;
    const value = latest.current;
    const token = ++generation.current;
    const jobs = requestedTargets.map(target => {
      const chosen = requested(target);
      const snapshots = new Map(chosen.map(unit => [unit.key, snapshot(value, unit, target)]));
      const sources = new Map(chosen.map(unit => [unit.key, unit]));
      const { batches, oversized } = translationBatches(
        chosen.map(unit => ({ key: unit.key, text: unit.block ? translationFragmentHTML(unit.block) : unit.text }))
      );
      for (const key of oversized)
        setErrors(previous => ({
          ...previous,
          [`${target}:${key}`]:
            'Deze eenheid is groter dan 5.000 tekens. Vertaal handmatig; tekst wordt niet afgekapt.',
        }));
      return { target, batches, snapshots, sources };
    });
    const total = jobs.reduce((sum, job) => sum + job.batches.reduce((count, batch) => count + batch.length, 0), 0);
    if (!total) {
      setNotice('Geen eenheden binnen dit bereik; grotere eenheden kunnen handmatig worden vertaald.');
      return;
    }
    requestActive.current = true;
    setProgress({ completed: 0, total });
    setLanguage(requestedTargets[0]);
    setStarted(previous => [...new Set([...previous, ...requestedTargets])]);
    try {
      for (const job of jobs)
        for (const fragments of job.batches) {
          if (!mounted.current || token !== generation.current) return;
          for (const fragment of fragments)
            setErrors(previous => {
              const next = { ...previous };
              delete next[`${job.target}:${fragment.key}`];
              return next;
            });
          try {
            const response = await api.post<{ translations: Partial<Record<ContentLanguage, Record<string, string>>> }>(
              `/${props.kind === 'blog' ? 'blog' : 'cases'}/translate`,
              { sourceLanguage: value.sourceLanguage, targets: [job.target], fragments }
            );
            if (!mounted.current || token !== generation.current) return;
            const additions: Proposal[] = [];
            for (const fragment of fragments) {
              const unit = job.sources.get(fragment.key)!;
              const result = response?.translations?.[job.target]?.[fragment.key];
              try {
                if (typeof result !== 'string' || !result.trim())
                  throw new Error('Geen bruikbare vertaling ontvangen. Je bestaande tekst blijft behouden.');
                if (unit.field && /<\/?[a-z][^>]*>/i.test(result))
                  throw new Error('Onverwachte opmaak in een tekstveld.');
                if (unit.key === 'field:seoDescription' && result.length > 320)
                  throw new Error('De SEO-beschrijving is langer dan 320 tekens. Kort deze handmatig in.');
                const proposal: Proposal = {
                  key: unit.key,
                  language: job.target,
                  snapshot: job.snapshots.get(unit.key)!,
                  value: unit.block ? translatedInlineBlock(result, unit.block) : result,
                  selected: true,
                };
                if (outdated(proposal, latest.current))
                  throw new Error(
                    'Voorstel verouderd: bron of vertaling is tijdens de aanvraag gewijzigd. Vraag opnieuw aan.'
                  );
                additions.push(proposal);
              } catch (error) {
                setErrors(previous => ({
                  ...previous,
                  [`${job.target}:${unit.key}`]: error instanceof Error ? error.message : 'Ongeldige vertaling.',
                }));
              }
            }
            setProposals(previous => [
              ...previous.filter(
                old => !additions.some(next => next.language === old.language && next.key === old.key)
              ),
              ...additions,
            ]);
          } catch (error) {
            if (!mounted.current || token !== generation.current) return;
            const message = error instanceof Error ? error.message : 'Vertalen is niet gelukt.';
            setErrors(previous => ({
              ...previous,
              ...Object.fromEntries(fragments.map(fragment => [`${job.target}:${fragment.key}`, message])),
            }));
          }
          setProgress(previous =>
            previous ? { ...previous, completed: previous.completed + fragments.length } : undefined
          );
        }
    } finally {
      requestActive.current = false;
      if (mounted.current && token === generation.current) setProgress(undefined);
    }
  };
  const confirmTranslation = () => {
    setDialog(false);
    setStarted(previous => [...new Set([...previous, ...targets])]);
    setLanguage(targets[0]);
    setFilter('all');
    if (method === 'manual') {
      setCompare(true);
      return;
    }
    void request(targets, scopedUnits);
  };
  const visible = (unit: Unit) => filter === 'all' || !unit.text.trim() || unitState(unit, language) === filter;
  const activeUnit = units.find(unit => unit.key === active);
  const currentCounts = counts(language);
  const languageProposals = proposals.filter(
    proposal => proposal.language === language && units.some(unit => unit.key === proposal.key && unit.text.trim())
  );
  const anyCopy = applicable.some(unit => unitState(unit, language) !== 'missing');
  const writingVisible = language === sourceLanguage || anyCopy || started.includes(language);
  const unitFrame = (unit: Unit, editable: ReactNode) => (
    <div
      key={unit.key}
      id={`translation-${unit.key}`}
      tabIndex={0}
      onFocus={() => setActive(unit.key)}
      className={`space-y-2 rounded-md p-2 ${unit.field && (!unit.field.halfWidth || (compare && language !== sourceLanguage)) ? 'md:col-span-2' : ''} ${active === unit.key ? 'ring-1 ring-primary' : ''}`}
    >
      {language !== sourceLanguage && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>
            {unit.label} · {unit.text.trim() ? states[unitState(unit, language)] : 'Optioneel'}
          </span>
          <span>
            {metadataFor(props, unit, language)?.origin === 'automatic'
              ? 'Automatisch vertaald'
              : metadataFor(props, unit, language)?.origin === 'manual'
                ? 'Handmatig'
                : unitState(unit, language) !== 'missing'
                  ? 'Historische copy · herkomst onbekend'
                  : ''}
          </span>
        </div>
      )}
      <div className={compare && language !== sourceLanguage ? 'grid gap-3 md:grid-cols-2' : ''}>
        {compare && language !== sourceLanguage && (
          <div className="rounded-md bg-muted p-3">
            <p className="mb-2 text-xs font-medium">Bron · {names[sourceLanguage]} · alleen lezen</p>
            {unit.block ? (
              <div
                onClick={event => event.preventDefault()}
                dangerouslySetInnerHTML={{ __html: translationFragmentHTML(unit.block) }}
              />
            ) : (
              <p className="whitespace-pre-wrap">{unit.text || 'Leeg bronveld'}</p>
            )}
          </div>
        )}
        <div>{editable}</div>
      </div>
      {errors[`${language}:${unit.key}`] && (
        <p role="alert" className="text-sm text-destructive">
          {errors[`${language}:${unit.key}`]} Handmatig bewerken blijft mogelijk.
        </p>
      )}
    </div>
  );
  const renderNode = (node: TiptapBlock, index: number): ReactNode => {
    const id = translationBlockId(node);
    const unit = units.find(item => item.key === id && item.block);
    if (unit) {
      if (!visible(unit)) return null;
      return unitFrame(
        unit,
        <InlineCopy
          key={`${language}:${unit.key}`}
          label={`${unit.label} · ${names[language]}`}
          source={node}
          value={translations[language]?.[unit.key]?.block}
          onChange={block => changeBlock(unit, block)}
          onFocus={editor => {
            setActive(unit.key);
            setInlineEditor(editor);
          }}
        />
      );
    }
    const children = Array.isArray(node.content)
      ? node.content.map((child, childIndex) => renderNode(child, childIndex))
      : null;
    const attrs = node.attrs && typeof node.attrs === 'object' ? (node.attrs as Record<string, unknown>) : {};
    if (node.type === 'image')
      return (
        <img
          key={index}
          src={String(attrs.src ?? '').replace(/^\/api\/public\/media\/([^?]+)(.*)$/, '/api/media/$1/file$2')}
          alt={String(attrs.alt ?? '')}
          className="my-4 w-full rounded-md"
        />
      );
    if (node.type === 'imageRow' && Array.isArray(attrs.images))
      return (
        <div key={index} className="my-4 grid grid-cols-2 gap-3">
          {attrs.images.map((image, imageIndex) =>
            image && typeof image === 'object' && 'src' in image ? (
              <img
                key={imageIndex}
                src={String(image.src).replace(/^\/api\/public\/media\/([^?]+)(.*)$/, '/api/media/$1/file$2')}
                alt={'alt' in image ? String(image.alt ?? '') : ''}
              />
            ) : null
          )}
        </div>
      );
    if (node.type === 'bulletList')
      return (
        <ul key={index} className="list-disc space-y-2 pl-6">
          {children}
        </ul>
      );
    if (node.type === 'orderedList')
      return (
        <ol
          key={index}
          start={typeof attrs.start === 'number' ? attrs.start : undefined}
          className="list-decimal space-y-2 pl-6"
        >
          {children}
        </ol>
      );
    if (node.type === 'listItem') return <li key={index}>{children}</li>;
    if (node.type === 'blockquote')
      return (
        <blockquote key={index} className="border-l-4 pl-4">
          {children}
        </blockquote>
      );
    if (node.type === 'horizontalRule') return <hr key={index} className="my-4" />;
    if (node.type === 'codeBlock')
      return (
        <pre key={index} className="overflow-auto rounded bg-muted p-3">
          {translationPlainText(node)}
        </pre>
      );
    return <div key={index}>{children}</div>;
  };

  if (!confirmed)
    return (
      <section className="space-y-5 rounded-md border p-6">
        <h2 className="text-xl font-semibold">In welke taal schrijf je het origineel?</h2>
        <label className="block space-y-2">
          <span>Originele taal</span>
          <select
            aria-label="Originele taal"
            value={sourceLanguage}
            onChange={event => props.onSourceLanguageChange(event.target.value as ContentLanguage)}
            className="block w-full rounded-md border bg-background p-3"
          >
            {languages.map(item => (
              <option key={item} value={item}>
                {names[item]}
              </option>
            ))}
          </select>
        </label>
        <p className="text-sm text-muted-foreground">
          De originele taal ligt vast zodra je begint te schrijven, ook als je de tekst later wist.
        </p>
        <Button
          onClick={() => {
            setConfirmed(true);
            setLanguage(sourceLanguage);
          }}
        >
          Start met schrijven
        </Button>
      </section>
    );

  return (
    <section className="space-y-6" style={{ '--content-header-height': `${headerHeight}px` } as CSSProperties}>
      <header
        ref={headerRef}
        className="sticky top-0 z-30 max-h-[45vh] space-y-3 overflow-y-auto rounded-md border bg-background/95 p-3 shadow-sm md:max-h-none md:overflow-visible"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm">
              Originele taal: <strong>{names[sourceLanguage]}</strong>
            </p>
            {props.draft.status === 'published' && <Badge variant="success">Gepubliceerd</Badge>}
          </div>
          {props.isNew && !locked && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmed(false)}>
              Originele taal wijzigen
            </Button>
          )}
        </div>
        <div role="tablist" aria-label="Taal van de inhoud" className="flex flex-wrap gap-2">
          {languages.map((item, index) => {
            const count = counts(item);
            const pendingCount = proposals.filter(
              proposal =>
                proposal.language === item && units.some(unit => unit.key === proposal.key && unit.text.trim())
            ).length;
            return (
              <button
                type="button"
                role="tab"
                aria-selected={item === language}
                tabIndex={item === language ? 0 : -1}
                key={item}
                onClick={() => {
                  setLanguage(item);
                  setInlineEditor(undefined);
                  setFilter('all');
                }}
                onKeyDown={event => {
                  let next: number;
                  if (event.key === 'ArrowRight') next = (index + 1) % languages.length;
                  else if (event.key === 'ArrowLeft') next = (index + languages.length - 1) % languages.length;
                  else if (event.key === 'Home') next = 0;
                  else if (event.key === 'End') next = languages.length - 1;
                  else return;
                  event.preventDefault();
                  setLanguage(languages[next]);
                  setInlineEditor(undefined);
                  setFilter('all');
                  const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="tab"]');
                  tabs?.[next]?.focus();
                }}
                className={`rounded-md border px-3 py-2 text-left text-sm ${item === language ? 'bg-primary text-primary-foreground' : ''}`}
              >
                <strong>{names[item]}</strong>
                {item !== sourceLanguage && (
                  <span className="block text-xs">
                    {count.missing} ontbreekt · {count.changed} bron gewijzigd · {count.unreviewed} nakijken ·{' '}
                    {count.reviewed} nagekeken{pendingCount ? ` · ${pendingCount} voorstellen` : ''}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="group" aria-label="Taalhulpmiddelen" className="flex flex-wrap items-center gap-3">
            {language !== sourceLanguage && (
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Switch checked={compare} onCheckedChange={setCompare} aria-label="Toon origineel" />
                Toon origineel
              </label>
            )}
            <Button variant="outline" disabled={Boolean(progress)} onClick={() => openDialog()}>
              Vertalingen maken
            </Button>
          </div>
          <div
            role="group"
            aria-label="Documentacties"
            className="ml-auto flex flex-wrap items-center justify-end gap-2"
          >
            <ContentPreview kind={props.kind} language={language} draft={props.draft} />
            {props.actions}
          </div>
        </div>
        {props.draft.status === 'published' && (
          <p className="text-xs text-muted-foreground">Wijzigingen worden bij opslaan zichtbaar op de website.</p>
        )}
        {languageProposals.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const review = document.getElementById('translation-proposals');
              review?.scrollIntoView({ block: 'start' });
              review?.focus({ preventScroll: true });
            }}
          >
            {languageProposals.length} voorstellen bekijken
          </Button>
        )}
        {(started.length > 0 || proposals.length > 0) &&
          languages
            .filter(item => item !== sourceLanguage)
            .some(item => {
              const count = counts(item);
              return count.missing + count.changed + count.unreviewed > 0;
            }) && (
            <p role="status" className="text-xs text-amber-700">
              Vertalingen zijn nog niet volledig nagekeken. Opslaan en de bestaande review/publicatieacties blijven
              beschikbaar.
            </p>
          )}
      </header>
      {progress && (
        <p role="status">
          Vertaalvoorstellen maken: {progress.completed}/{progress.total} eenheden. Je kunt ondertussen verder
          schrijven.
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-md bg-muted p-3">
          {notice}
        </p>
      )}
      {undo && (
        <Button variant="outline" onClick={undoAcceptance}>
          Laatste overname ongedaan maken
        </Button>
      )}
      {!writingVisible && (
        <div className="space-y-3 rounded-md border p-4">
          <h2 className="font-semibold">Nog geen vertaling</h2>
          <p>Doeltaal: {names[language]}. Broninhoud wordt nooit als vertaling ingevuld.</p>
          <Button
            variant="outline"
            onClick={() => {
              openDialog(language);
              setMethod('manual');
            }}
          >
            Handmatig starten
          </Button>{' '}
          <Button onClick={() => openDialog(language)}>Automatisch starten</Button>
        </div>
      )}
      {languageProposals.length > 0 && (
        <section
          id="translation-proposals"
          tabIndex={-1}
          style={{ scrollMarginTop: headerHeight + 16 }}
          aria-label="Vertaalvoorstellen"
          className="space-y-4 rounded-md border p-4"
        >
          <h2 className="text-lg font-semibold">Voorstellen · {names[language]}</h2>
          <p>Vergelijk met de huidige copy. Overnemen verandert alleen het concept, niet de opgeslagen versie.</p>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={!languageProposals.some(proposal => proposal.selected && !outdated(proposal))}
              onClick={() => accept(languageProposals.filter(proposal => proposal.selected))}
            >
              Geselecteerde voorstellen overnemen
            </Button>
            <Button
              variant="outline"
              onClick={() => setProposals(previous => previous.filter(proposal => proposal.language !== language))}
            >
              Voorstellen afwijzen / huidige copy behouden
            </Button>
          </div>
          {languageProposals.map(proposal => {
            const unit = units.find(unit => unit.key === proposal.key)!;
            const stale = outdated(proposal);
            const current = targetFor(props, unit, language);
            return (
              <article key={proposal.key} className="space-y-3 rounded-md border p-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={proposal.selected}
                    onChange={event =>
                      setProposals(previous =>
                        previous.map(item => (item === proposal ? { ...item, selected: event.target.checked } : item))
                      )
                    }
                  />
                  {unit.label}: {unit.text.slice(0, 60)}
                </label>
                {stale && <p role="alert">Verouderd voorstel — bron of copy gewijzigd. Vraag opnieuw aan.</p>}
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium">Huidige vertaling</p>
                    {typeof current === 'string' ? (
                      <p>{current || 'Ontbreekt'}</p>
                    ) : current ? (
                      <div
                        onClick={event => event.preventDefault()}
                        dangerouslySetInnerHTML={{ __html: translationFragmentHTML(current) }}
                      />
                    ) : (
                      <p>Ontbreekt</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium">Voorgestelde vertaling</p>
                    {typeof proposal.value === 'string' ? (
                      <p>{proposal.value}</p>
                    ) : (
                      <div
                        onClick={event => event.preventDefault()}
                        dangerouslySetInnerHTML={{ __html: translationFragmentHTML(proposal.value) }}
                      />
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled={stale} onClick={() => accept([proposal])}>
                    Overnemen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setProposals(previous => previous.filter(item => item !== proposal))}
                  >
                    Afwijzen / behouden
                  </Button>
                </div>
              </article>
            );
          })}
        </section>
      )}
      {writingVisible && (
        <>
          {language !== sourceLanguage && (
            <div
              style={{ top: headerHeight }}
              className="sticky z-20 flex max-h-[35vh] flex-wrap items-center gap-2 overflow-y-auto rounded-md border bg-background p-2 md:max-h-none md:overflow-visible"
              aria-label="Acties voor geselecteerde tekst"
            >
              <label className="text-sm">
                Filter{' '}
                <select
                  aria-label="Vertaalstatus filter"
                  value={filter}
                  onChange={event => setFilter(event.target.value as typeof filter)}
                  className="rounded border bg-background p-2"
                >
                  <option value="all">Volledige tekst</option>
                  {(['missing', 'changed', 'unreviewed'] as const).map(state => (
                    <option key={state} value={state}>
                      {states[state]} ({currentCounts[state]})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                Ga naar{' '}
                <select
                  aria-label="Ga naar teksteenheid"
                  value={active ?? ''}
                  onChange={event => {
                    const key = event.target.value;
                    setActive(key);
                    document.getElementById(`translation-${key}`)?.focus();
                  }}
                  className="max-w-48 rounded border bg-background p-2"
                >
                  <option value="">Kies tekst</option>
                  {units.filter(visible).map((unit, index) => (
                    <option key={unit.key} value={unit.key}>
                      {index + 1}. {unit.label}: {unit.text.slice(0, 35)}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                variant="ghost"
                size="sm"
                disabled={!inlineEditor || inlineEditor.isDestroyed}
                onClick={() => inlineEditor?.chain().focus().toggleBold().run()}
                aria-label="Vet"
              >
                <b>B</b>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!inlineEditor || inlineEditor.isDestroyed}
                onClick={() => inlineEditor?.chain().focus().toggleItalic().run()}
                aria-label="Cursief"
              >
                <i>I</i>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!inlineEditor || inlineEditor.isDestroyed || inlineEditor.isActive('heading')}
                onClick={() => inlineEditor?.chain().focus().setColor('#939393').run()}
              >
                Standaard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!inlineEditor || inlineEditor.isDestroyed || inlineEditor.isActive('heading')}
                onClick={() => inlineEditor?.chain().focus().setColor('#222222').run()}
              >
                Donker
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!activeUnit?.text.trim() || Boolean(progress)}
                onClick={() => activeUnit && void request([language], () => [activeUnit])}
              >
                {activeUnit && unitState(activeUnit, language) === 'missing'
                  ? 'Vertaal deze tekst'
                  : `Opnieuw vertalen vanuit ${names[sourceLanguage]}`}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!activeUnit?.text.trim() || (activeUnit && unitState(activeUnit, language) === 'missing')}
                onClick={() => activeUnit && approve([activeUnit])}
              >
                {activeUnit && unitState(activeUnit, language) === 'changed'
                  ? 'Bevestig bestaande vertaling'
                  : 'Markeer als nagekeken'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!applicable.length || currentCounts.missing > 0 || currentCounts.changed > 0}
                onClick={() => approve(applicable)}
              >
                Hele taal nagekeken
              </Button>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {fields.map(field => {
              const unit = units.find(unit => unit.key === field.key)!;
              if (language !== sourceLanguage && !visible(unit)) return null;
              const FieldInput = field.multiline ? Textarea : Input;
              return unitFrame(
                unit,
                <label className="block space-y-2">
                  <span className="font-medium">{field.label}</span>
                  <FieldInput
                    value={field.value[language] ?? ''}
                    aria-label={`${field.label} · ${names[language]}`}
                    maxLength={field.key === 'field:seoDescription' ? 320 : undefined}
                    placeholder={language !== sourceLanguage ? 'Ontbrekende vertaling — schrijf hier' : undefined}
                    onFocus={() => {
                      setActive(field.key);
                      setInlineEditor(undefined);
                    }}
                    onChange={event => changeField(field, event.target.value)}
                  />
                </label>
              );
            })}
          </div>
          <div className="space-y-3 [&_.sticky]:top-[var(--content-header-height)]">
            <h2 className="font-medium">{props.kind === 'blog' ? 'Artikelinhoud' : 'Case-inhoud'}</h2>
            {language === sourceLanguage ? (
              <RichTextEditor
                value={content}
                onChange={value => {
                  const nodes = Array.isArray(value.content) ? value.content : [];
                  const layoutAuthored =
                    nodes.length > 1 ||
                    nodes.some(node => node && typeof node === 'object' && 'type' in node && node.type !== 'paragraph');
                  if (translationPlainText(value).trim() || layoutAuthored) setLocked(true);
                  props.onContentChange(value);
                }}
              />
            ) : (
              renderNode(content, 0)
            )}
          </div>
        </>
      )}
      <details className="rounded-md border p-4">
        <summary className="cursor-pointer font-semibold">Pagina-instellingen</summary>
        <div className="mt-4 space-y-5">{props.settings}</div>
      </details>
      {dialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4"
          onKeyDown={event => {
            if (event.key === 'Escape') setDialog(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="translation-dialog-title"
            className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-md bg-background p-6"
          >
            <h2 id="translation-dialog-title" className="text-xl font-semibold">
              Vertalingen maken
            </h2>
            <p>Bron: {names[sourceLanguage]}</p>
            <fieldset className="space-y-2">
              <legend>Doeltalen</legend>
              {languages
                .filter(item => item !== sourceLanguage)
                .map(item => (
                  <label key={item} className="flex gap-2">
                    <input
                      type="checkbox"
                      checked={targets.includes(item)}
                      onChange={event =>
                        setTargets(previous =>
                          event.target.checked ? [...previous, item] : previous.filter(target => target !== item)
                        )
                      }
                    />
                    {names[item]} · {scopedUnits(item).length} eenheden
                  </label>
                ))}
            </fieldset>
            <fieldset className="flex flex-wrap gap-4">
              <legend>Methode</legend>
              {(['manual', 'automatic'] as const).map(item => (
                <label key={item}>
                  <input
                    type="radio"
                    name="translation-method"
                    checked={method === item}
                    onChange={() => setMethod(item)}
                  />{' '}
                  {item === 'manual' ? 'Handmatig schrijven (geen vertaalverzoek)' : 'Automatische vertaalvoorstellen'}
                </label>
              ))}
            </fieldset>
            {method === 'automatic' && (
              <>
                <label className="block space-y-2">
                  Bereik
                  <select
                    value={scope}
                    aria-label="Bereik van vertaling"
                    onChange={event => setScope(event.target.value as typeof scope)}
                    className="block w-full rounded border bg-background p-2"
                  >
                    <option value="missing">Alleen ontbrekende tekst</option>
                    <option value="changed">Alleen bron-gewijzigde tekst</option>
                    <option value="selected">Expliciet geselecteerde bestaande/ontbrekende tekst</option>
                  </select>
                </label>
                {scope === 'selected' && (
                  <fieldset className="max-h-48 space-y-2 overflow-auto rounded border p-2">
                    <legend>Teksteenheden</legend>
                    {applicable.map(unit => (
                      <label key={unit.key} className="flex gap-2">
                        <input
                          type="checkbox"
                          checked={selectedUnits.includes(unit.key)}
                          onChange={event =>
                            setSelectedUnits(previous =>
                              event.target.checked ? [...previous, unit.key] : previous.filter(key => key !== unit.key)
                            )
                          }
                        />
                        {unit.label}: {unit.text.slice(0, 60)}
                      </label>
                    ))}
                  </fieldset>
                )}
                <p className="text-sm">
                  {targets.map(target => `${names[target]}: ${scopedUnits(target).length} eenheden`).join(' · ')}.
                  Bestaande tekst wordt niet vervangen zonder overname. Maximaal 5.000 tekens per eenheid.
                </p>
              </>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <Button autoFocus variant="outline" onClick={() => setDialog(false)}>
                Annuleren
              </Button>
              <Button
                disabled={
                  !targets.length ||
                  Boolean(progress) ||
                  (method === 'automatic' && !targets.some(target => scopedUnits(target).length))
                }
                onClick={confirmTranslation}
              >
                {method === 'automatic'
                  ? 'Maak vertaalvoorstellen'
                  : `Start ${targets.map(target => names[target]).join(' en ')} vertaling`}
              </Button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
